import { useEffect, useState } from 'react'
import { Button, Input, Label, Modal, Radio, Text, Textarea } from '@nimbus-ds/components'
import type { Experiment } from '@/types'
import type { CloneExperimentRequest } from '@/api/experiments'

/**
 * "Repetir" — relaunch an experiment with the exact same configuration
 * (CLONE-EXPERIMENT-SPEC.md §7.2). The source is finalized and archived by the
 * backend; its results stay on record. Reasons are free text, never an enum.
 */

export type RepeatTiming = NonNullable<CloneExperimentRequest['timing']>

export interface RepeatSubmit {
  name: string
  note: string
  timing: RepeatTiming
  launch: boolean
}

interface Props {
  open: boolean
  experiment: Experiment | null
  busy?: boolean
  onDismiss: () => void
  onSubmit: (data: RepeatSubmit) => void
}

/** Mirrors the backend default: "<name> (v2)", "(vN)" → "(vN+1)". */
export function nextVersionName(name: string): string {
  const base = (name || '').trim()
  const m = base.match(/^(.*?)\s*\(v(\d+)\)$/i)
  if (m) return `${m[1].trim()} (v${Number(m[2]) + 1})`
  return `${base} (v2)`
}

const hasDuration = (e: Experiment | null) =>
  !!(e?.timing?.start_date_time && e?.timing?.end_date_time)

export function RepeatExperimentModal({ open, experiment, busy, onDismiss, onSubmit }: Props) {
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [timing, setTiming] = useState<RepeatTiming>('same_duration')

  // Reset per source so a stale name from the previous modal never leaks.
  useEffect(() => {
    if (open && experiment) {
      setName(nextVersionName(experiment.name))
      setNote('')
      setTiming(hasDuration(experiment) ? 'same_duration' : 'none')
    }
  }, [open, experiment?.id])

  if (!experiment) return null

  const isOptimization = experiment.mode === 'optimization'
  const wasRunning = experiment.status === 'active' || experiment.status === 'paused'
  const submit = (launch: boolean) =>
    onSubmit({ name: name.trim() || nextVersionName(experiment.name), note: note.trim(), timing, launch })

  return (
    <Modal open={open} onDismiss={() => !busy && onDismiss()}>
      <Modal.Header title="Repetir experimento" />
      <Modal.Body>
        <div className="space-y-4">
          <Text>
            Se crea una copia exacta de <strong>{experiment.name}</strong> — mismas variantes,
            audiencia, objetivo y nivel de riesgo — lista para correr de nuevo.
          </Text>

          <div className="space-y-2">
            <Label htmlFor="repeat-name">Nombre</Label>
            <Input
              id="repeat-name"
              name="repeat-name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="repeat-note">¿Por qué se repite? (opcional)</Label>
            <Textarea
              id="repeat-note"
              name="repeat-note"
              placeholder="ej. el paso del objetivo apuntaba a la URL vieja del carrito"
              value={note}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
              lines={2}
            />
            <Text fontSize="caption" color="neutral-textLow">
              Queda anotado en la nueva corrida, junto al enlace a la anterior.
            </Text>
          </div>

          {!isOptimization && (
            <div className="space-y-2">
              <Label>Fechas</Label>
              <div className="flex flex-col gap-2">
                <Radio
                  name="repeat-timing"
                  id="repeat-timing-same"
                  value="same_duration"
                  label={hasDuration(experiment) ? 'Misma duración, desde hoy' : 'Empezar hoy, sin fecha de fin'}
                  checked={timing === 'same_duration'}
                  onChange={() => setTiming('same_duration')}
                />
                <Radio
                  name="repeat-timing"
                  id="repeat-timing-none"
                  value="none"
                  label="Sin fechas programadas"
                  checked={timing === 'none'}
                  onChange={() => setTiming('none')}
                />
              </div>
            </div>
          )}

          <Text fontSize="caption" color="neutral-textLow">
            {wasRunning
              ? 'El original se detiene y se archiva; sus resultados quedan guardados tal como están.'
              : 'El original se archiva; sus resultados quedan guardados tal como están.'}
          </Text>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance="neutral" onClick={onDismiss} disabled={busy}>
          Cancelar
        </Button>
        <Button appearance="neutral" onClick={() => submit(false)} disabled={busy}>
          Guardar borrador
        </Button>
        <Button appearance="primary" onClick={() => submit(true)} disabled={busy}>
          {busy ? 'Creando...' : 'Crear y lanzar'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
